/* eslint-disable prefer-const */
/**
 * @Relay no-runtime
 */
/**
 * @graphql cc
 */
import graphql from 'graphql-tag';
import { useState, useEffect } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import memoizeOne from 'memoize-one';

import type { InvoiceRequest } from '@atlassian/commerce-ccp-gateway-client';
import { COMMERCE_BACKEND_CCP, COMMERCE_BACKEND_HAMS } from '@atlassian/commerce-ui/rollout';

import type { useIsPreDunningPeriodTrialDetailsQuery as useIsPreDunningPeriodTrialDetailsQuery$data } from './__types__/useIsPreDunningPeriodTrialDetailsQuery.ts';
import { useCommerceBackendName } from './useCommerceBackendName.ts';
import { useInvoiceRequestV2 } from './useInvoiceRequestV2.ts';
import { useHasBillingInformation } from './useHasBillingInformation.ts';
import { differenceInMilliseconds } from 'date-fns';

export interface DeactivationDateResult {
	date: Date;
	dateString: string;
	daysLeftUntilDeactivation?: number;
}

export type PreDunningErrorSource =
	| 'commerce-backend-name'
	| 'billing-info'
	| 'trial-details'
	| 'invoice-request';

export interface UseInvoiceRequestResponse {
	isPreDunningPeriod: boolean;
	deactivationDate: DeactivationDateResult | null;
	loading: boolean;
	error: Error | null;
	errorSource?: PreDunningErrorSource | null;
}

const DAYS_TO_DEACTIVATION = 14;

/**
 * [HAMS]
 */
export const getDeactivationDate = (
	timestamp: string,
	daysToAdd?: number,
): DeactivationDateResult => {
	const date = new Date(timestamp);
	if (daysToAdd) {
		date.setDate(date.getDate() + daysToAdd);
	}

	const difference = differenceInMilliseconds(date, new Date()) / 86400000;

	let daysLeftUntilDeactivation = difference > 0 && difference < 1 ? 0 : Math.round(difference);

	const dateString = new Date(date).toLocaleDateString(undefined, {
		year: 'numeric',
		month: 'long',
		day: 'numeric',
	});

	return { date, dateString, daysLeftUntilDeactivation };
};

/**
 * [CCP] Check the invoice request data to see if it falls
 * within the pre-dunning period and has the right entitlement
 */
const getEligiblePreDunningInvoiceRequests = (
	ir: InvoiceRequest,
	entitlementId: string,
): boolean => {
	const { status, items } = ir;
	const preDunningAttempts = ir.preDunning?.preDunningAttempts;

	return (
		// determines the pre-dunning status
		// filter out non-Confluence entitlement ids
		status === 'OPEN' &&
		Boolean(preDunningAttempts?.length) &&
		Boolean(ir.createdAt) &&
		// @ts-ignore - outdated CCP REST autogenerated client
		!ir.invoicedAt &&
		// @ts-ignore - outdated CCP REST autogenerated client
		!ir.uninvoiceableAt &&
		// @ts-ignore - outdated CCP REST autogenerated client
		new Date() < new Date(ir.preDunning?.endAt) &&
		Boolean(items?.some((item) => item.entitlementId === entitlementId))
	);
};

/**
 * Extract the invoice with the earliest startedAt date
 * to calculate the deactivation date
 */
const getEarliestPreDunningInvoiceRequest = (invoices: InvoiceRequest[]): InvoiceRequest | null => {
	if (!invoices.length) {
		return null;
	}

	return invoices.reduce((prev: InvoiceRequest, current: InvoiceRequest) => {
		// we filter for eligible invoice requests which ensures startedAt is not empty. TS doesn't like the non-null assertion (!)
		// @ts-ignore
		return prev.preDunning.startedAt < current.preDunning.startedAt ? prev : current;
	});
};

const memoizedGetEarliestPreDunningInvoiceRequest = memoizeOne(getEarliestPreDunningInvoiceRequest);

export const useIsPreDunningPeriod = (
	{ skip }: { skip: boolean } = { skip: false },
): UseInvoiceRequestResponse => {
	const [isPreDunningPeriod, setIsPreDunningPeriod] = useState(false);
	const [deactivationDate, setDeactivationDate] = useState<DeactivationDateResult | null>(null);

	const {
		commerceBackendName,
		loading: commerceBackendNameLoading,
		error: commerceBackendNameError,
	} = useCommerceBackendName(skip);

	const skipTrialDetails = skip || commerceBackendName === COMMERCE_BACKEND_CCP;

	const {
		data: trialDetailsData,
		loading: trialLoading,
		error: trialErr,
	} = useQuery<useIsPreDunningPeriodTrialDetailsQuery$data>(
		graphql`
			query useIsPreDunningPeriodTrialDetailsQuery {
				license {
					billingSourceSystem
					licenseConsumingUserCount
					trialEndDate
				}
			}
		`,
		{
			skip: skipTrialDetails,
		},
	);

	const skipInvoiceRequest = skip || commerceBackendName === COMMERCE_BACKEND_HAMS;

	const {
		entitlementId,
		invoiceRequestData,
		loading: invoiceRequestLoading,
		error: invoiceRequestError,
	} = useInvoiceRequestV2({
		skip: skipInvoiceRequest,
	});

	//Make this call only if user is in PreDunning Period
	const {
		hasBillingInformation,
		loading: billingLoading,
		error: billingError,
	} = useHasBillingInformation(!isPreDunningPeriod || skip);

	const hasBilling = !billingLoading && !billingError && hasBillingInformation;

	const allLoading =
		commerceBackendNameLoading || billingLoading || trialLoading || invoiceRequestLoading;
	const allSkip = skipTrialDetails || skipInvoiceRequest;

	let allError: Error | null = null;
	let errorSource: PreDunningErrorSource | null = null;
	if (commerceBackendNameError) {
		allError = commerceBackendNameError;
		errorSource = 'commerce-backend-name';
	} else if (billingError) {
		allError = billingError;
		errorSource = 'billing-info';
	} else if (trialErr) {
		allError = trialErr;
		errorSource = 'trial-details';
	} else if (invoiceRequestError) {
		allError = invoiceRequestError;
		errorSource = 'invoice-request';
	}

	useEffect(() => {
		if ((!allLoading || !allError) && deactivationDate === null) {
			if (commerceBackendName === COMMERCE_BACKEND_HAMS) {
				const { trialEndDate } = trialDetailsData?.license || {};
				if (trialEndDate) {
					const deactivationDateResult = getDeactivationDate(trialEndDate, DAYS_TO_DEACTIVATION);

					const today = new Date();
					if (
						new Date(trialEndDate) < today &&
						deactivationDateResult &&
						today < deactivationDateResult.date
					) {
						setIsPreDunningPeriod(true);
						setDeactivationDate(deactivationDateResult);
					}
				}
			} else if (commerceBackendName === COMMERCE_BACKEND_CCP) {
				const data = invoiceRequestData?.data;
				if (data && entitlementId) {
					const eligibleInvoices = data.filter((invoice) =>
						getEligiblePreDunningInvoiceRequests(invoice, entitlementId),
					);
					const earliestPreDunningInvoice: InvoiceRequest | null =
						memoizedGetEarliestPreDunningInvoiceRequest(eligibleInvoices);

					// @ts-ignore - outdated CCP REST autogenerated client
					const projectedEndDate = earliestPreDunningInvoice?.preDunning?.endAt;

					if (earliestPreDunningInvoice && projectedEndDate) {
						setIsPreDunningPeriod(true);
						setDeactivationDate(getDeactivationDate(projectedEndDate));
					}
				}
			}
		}
	}, [
		allLoading,
		allError,
		commerceBackendName,
		entitlementId,
		trialDetailsData,
		invoiceRequestData,
		deactivationDate,
	]);

	if (allLoading || allError || hasBilling) {
		return {
			isPreDunningPeriod: false,
			deactivationDate: null,
			error: allError,
			loading: allLoading,
			errorSource,
		};
	}

	return {
		isPreDunningPeriod,
		deactivationDate,
		error: allError,
		loading: allSkip ? false : allLoading,
		errorSource,
	};
};
